/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Switch, FormControlLabel, Checkbox, FormGroup as MuiFormGroup } from '@mui/material';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { 
  StepContainer, 
  StepTitle, 
  FormGroup, 
  ValueDisplay, 
  ValueRow,
  PercentageInputGroup,
  CheckboxGroup,
  CheckboxLabel,
  HelpText,
  ErrorText
} from './styles';

// Mapeamento dos principais códigos de banco para nomes
const bankNames = {
  '001': 'Banco do Brasil',
  '033': 'Santander',
  '104': 'Caixa Econômica',
  '237': 'Bradesco',
  '341': 'Itaú',
  '077': 'Inter',
  '260': 'Nubank',
  '290': 'PagBank',
  '336': 'C6 Bank',
  '212': 'Banco Original',
  '756': 'Sicoob',
  '748': 'Sicredi',
  '422': 'Safra',
  '070': 'BRB',
  '655': 'Neon',
  '323': 'Mercado Pago',
  '380': 'PicPay',
  '403': 'Cora',
};

const getBankName = (bankCode) => {
  return bankNames[bankCode] || `Banco ${bankCode}`;
};

const formatAccountSummary = (account) => {
  if (!account) return 'Conta padrão da estação';
  
  const holderName =
    account.holder_name ||
    account.bank_account_account_holder_name ||
    account.legal_representative_name ||
    account.name ||
    '';
  const bankCode =
    account.bank_code ||
    account.bankAccountBankNumber ||
    account.bank_account_bank_number ||
    account.bankCode ||
    '';
  const agency = account.agency || account.agencia || account.bank_account_agency_number || '';
  const accountNumber =
    account.account_number ||
    account.conta ||
    account.bank_account_account_number ||
    '';
  const accountDv =
    account.account_dv ||
    account.conta_dv ||
    account.bank_account_complement_number ||
    '';
  
  if (bankCode && agency && accountNumber) {
    return `${holderName ? holderName + ' | ' : ''}${getBankName(bankCode)} | Ag: ${agency} | Cc: ${accountNumber}${accountDv ? '-' + accountDv : ''}`;
  }
  
  return holderName || 'Conta padrão da estação';
};

const renderTextField = ({ input, label, meta: { touched, error }, ...custom }) => {
  return (
    <TextField
      label={label}
      variant="outlined"
      error={touched && Boolean(error)}
      helperText={touched && error}
      name={input.name}
      value={input.value || ''}
      onChange={(e) => input.onChange(e.target.value)}
      onBlur={(e) => input.onBlur(e.target.value)}
      onFocus={input.onFocus}
      {...custom}
      fullWidth
    />
  );
};

const renderSelectField = ({ input, label, meta: { touched, error }, children, ...custom }) => (
  <TextField
    select
    label={label}
    variant="outlined"
    error={touched && Boolean(error)}
    helperText={touched && error}
    name={input.name}
    value={input.value || ''}
    onChange={(e) => input.onChange(e.target.value)}
    onBlur={(e) => input.onBlur(e.target.value)}
    onFocus={input.onFocus}
    {...custom}
    fullWidth
  >
    {children}
  </TextField>
);

const RECURRENCE_OPTIONS = [
  { value: 'single', label: 'Pagamento único' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'bimonthly', label: 'Bimestral' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'annual', label: 'Anual' },
];

const Valores = ({ formValues, change, stationAccount }) => {
  const [linkValue, setLinkValue] = useState(parseFloat(formValues?.link_value) || 0);
  const [partnerPercentage, setPartnerPercentage] = useState(parseFloat(formValues?.partner_percentage) || 0);
  const [stationPercentage, setStationPercentage] = useState(parseFloat(formValues?.station_percentage) || 0);
  const [operationFee, setOperationFee] = useState(0);
  const [netValue, setNetValue] = useState(0);
  const [allowInstallments, setAllowInstallments] = useState(formValues?.allow_installments || false);
  const [paymentMethods, setPaymentMethods] = useState(formValues?.payment_methods || []);
  const [percentageError, setPercentageError] = useState('');
  const [recurrenceType, setRecurrenceType] = useState(formValues?.recurrence_type || 'single');
  const hasSelectedPartner = Boolean(
    formValues?.has_partner &&
      formValues?.partner_user_id &&
      formValues?.partner_establishment_id
  );

  useEffect(() => {
    // Calcular valores usando a taxa de administração da station + taxa fixa
    const value = parseFloat(linkValue) || 0;
    const taxRate = parseFloat(stationAccount?.administration_tax) || 0;
    const fixedFee = 1.50; // Taxa fixa por transação
    const percentageFee = value * (taxRate / 100); // Taxa de administração da station
    const totalFee = percentageFee + fixedFee;
    const net = value - totalFee;
    
    setOperationFee(totalFee);
    setNetValue(net);
  }, [linkValue, stationAccount?.administration_tax]);

  useEffect(() => {
    // Validar percentuais
    const total = partnerPercentage + stationPercentage;
    if (hasSelectedPartner && total !== 100 && total > 0) {
      setPercentageError('A soma dos percentuais deve ser 100%');
    } else {
      setPercentageError('');
    }
  }, [hasSelectedPartner, partnerPercentage, stationPercentage]);

  useEffect(() => {
    if (!hasSelectedPartner) {
      if (partnerPercentage !== 0) {
        setPartnerPercentage(0);
        change('partner_percentage', 0);
      }

      if (stationPercentage !== 100) {
        setStationPercentage(100);
        change('station_percentage', 100);
      }
    }
  }, [change, hasSelectedPartner, partnerPercentage, stationPercentage]);

  const handleLinkValueChange = (event) => {
    const value = event.target.value;

    // Permitir campo vazio enquanto o usuário digita
    if (value === '') {
      setLinkValue(0);
      if (change) {
        change('link_value', 0);
      }
      return;
    }

    // Aceitar vírgula ou ponto como separador decimal
    const cleanValue = value.replace(',', '.');
    const numValue = parseFloat(cleanValue);

    if (Number.isNaN(numValue)) {
      return;
    }

    setLinkValue(numValue);
    if (change) {
      change('link_value', numValue);
    }
  };

  const handlePartnerPercentageChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    if (value >= 0 && value <= 100) {
      setPartnerPercentage(value);
      setStationPercentage(100 - value);
      change('partner_percentage', value);
      change('station_percentage', 100 - value);
    }
  };

  const handleStationPercentageChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    if (value >= 0 && value <= 100) {
      setStationPercentage(value);
      setPartnerPercentage(100 - value);
      change('station_percentage', value);
      change('partner_percentage', 100 - value);
    }
  };

  const handlePaymentMethodChange = (method) => {
    let newMethods = [...paymentMethods];
    if (newMethods.includes(method)) {
      newMethods = newMethods.filter(m => m !== method);
    } else {
      newMethods.push(method);
    }
    setPaymentMethods(newMethods);
    change('payment_methods', newMethods);
  };

  const handleInstallmentsChange = (event) => {
    const value = event.target.checked;
    setAllowInstallments(value);
    change('allow_installments', value);
  };

  const handleRecurrenceChange = (event) => {
    const value = event.target.value;
    setRecurrenceType(value);
    change('recurrence_type', value);

    // Pagamentos recorrentes aceitam apenas cartão de crédito
    if (value !== 'single') {
      setPaymentMethods(['cartao']);
      change('payment_methods', ['cartao']);
      setAllowInstallments(false);
      change('allow_installments', false);
    }
  };

  const isRecurring = recurrenceType !== 'single';

  useEffect(() => {
    if (isRecurring && allowInstallments) {
      setAllowInstallments(false);
      change('allow_installments', false);
    }
  }, [allowInstallments, change, isRecurring]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <StepContainer>
      <StepTitle>Valores</StepTitle>
      
      <FormGroup>
        <TextField
          label="Valor do link *"
          variant="outlined"
          placeholder="0,00"
          fullWidth
          required
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={linkValue === 0 ? '' : linkValue}
          onChange={handleLinkValueChange}
          InputProps={{
            startAdornment: <span style={{ marginRight: '8px', color: '#666' }}>R$</span>,
          }}
        />
      </FormGroup>

      <FormGroup>
        <TextField
          select
          label="Tipo de Pagamento"
          variant="outlined"
          fullWidth
          value={recurrenceType}
          onChange={handleRecurrenceChange}
        >
          {RECURRENCE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        {isRecurring && (
          <HelpText style={{ color: '#f57c00' }}>
            Pagamentos recorrentes aceitam apenas cartão de crédito.
          </HelpText>
        )}
      </FormGroup>

      {hasSelectedPartner && (
        <PercentageInputGroup>
          <FormGroup>
            <TextField
              label="Percentual parceiro *"
              variant="outlined"
              placeholder="Digite um valor de 0 a 100"
              fullWidth
              required
              type="number"
              value={partnerPercentage}
              onChange={handlePartnerPercentageChange}
              inputProps={{ min: "0", max: "100" }}
            />
            <HelpText>Somado com o percentual da estação tem que dar 100</HelpText>
          </FormGroup>

          <FormGroup>
            <TextField
              label="Percentual do estação *"
              variant="outlined"
              placeholder="Digite um valor de 0 a 100"
              fullWidth
              required
              type="number"
              value={stationPercentage}
              onChange={handleStationPercentageChange}
              inputProps={{ min: "0", max: "100" }}
            />
            <HelpText>Somado com o percentual do parceiro tem que dar 100</HelpText>
          </FormGroup>
        </PercentageInputGroup>
      )}

      {percentageError && <ErrorText>{percentageError}</ErrorText>}

      {/* Resumo das contas que recebem o valor do link */}
      {stationAccount && (
        <ValueDisplay>
          <ValueRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
            <strong>Conta padrão da estação ({hasSelectedPartner ? stationPercentage || 0 : 100}%):</strong>
            <span style={{ fontSize: '13px' }}>
              {formatAccountSummary(stationAccount)}
            </span>
          </ValueRow>
          {hasSelectedPartner && (
            <ValueRow style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px', marginTop: '12px' }}>
              <strong>Conta do parceiro - Link ({partnerPercentage || 0}%):</strong>
              <span style={{ fontSize: '13px' }}>
                {formValues?.partner_account_summary ||
                  formValues?.partner_establishment_name ||
                  formValues?.partner_name ||
                  'Conta do parceiro'}
              </span>
            </ValueRow>
          )}
        </ValueDisplay>
      )}

      <FormGroup>
        <label style={{ fontSize: '14px', fontWeight: 500, color: '#555', marginBottom: '8px', display: 'block' }}>
          Opções de pagamento *
        </label>
        <CheckboxGroup>
          <CheckboxLabel>
            <Checkbox
              checked={paymentMethods.includes('boleto')}
              onChange={() => handlePaymentMethodChange('boleto')}
              color="primary"
              disabled={isRecurring}
            />
            Boleto
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox
              checked={paymentMethods.includes('cartao')}
              onChange={() => handlePaymentMethodChange('cartao')}
              color="primary"
              disabled={isRecurring}
            />
            Cartão (crédito/débito)
          </CheckboxLabel>
          <CheckboxLabel>
            <Checkbox
              checked={paymentMethods.includes('pix')}
              onChange={() => handlePaymentMethodChange('pix')}
              color="primary"
              disabled={isRecurring}
            />
            PIX
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Pode selecionar mais de um ou todos, para que o pagador decida</HelpText>
      </FormGroup>

      <FormGroup>
        <Field
          name="rescue_options"
          component={renderSelectField}
          label="Opções de resgate"
          fullWidth
        >
          <MenuItem value="">Selecione uma opção</MenuItem>
          <MenuItem value="todos">Todos</MenuItem>
          <MenuItem value="parcial">Parcial</MenuItem>
          <MenuItem value="total">Total</MenuItem>
        </Field>
      </FormGroup>

      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={allowInstallments}
              onChange={handleInstallmentsChange}
              color="primary"
              disabled={isRecurring}
            />
          }
          label="Permite parcelamento?"
        />
      </FormGroup>

      <ValueDisplay>
        <ValueRow>
          <strong>Valor líquido:</strong>
          <span>{formatCurrency(netValue)}</span>
        </ValueRow>
        <ValueRow>
          <strong>Custo de operação ({stationAccount?.administration_tax || 0}% + R$ 1,50):</strong>
          <span>{formatCurrency(operationFee)}</span>
        </ValueRow>
      </ValueDisplay>

      <HelpText style={{ marginTop: '16px' }}>
        * Somente para informação, não é alterável. Taxa de administração configurada na estação + taxa fixa de R$ 1,50 por transação.
      </HelpText>
    </StepContainer>
  );
};

Valores.propTypes = {
  formValues: PropTypes.object,
  change: PropTypes.func.isRequired,
  stationAccount: PropTypes.object,
};

export default Valores;
